import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { SideMenuContentComponent } from "@shared/components/side-menu/components/side-menu-content/side-menu-content.component";
import { SideMenuGroupComponent } from "@shared/components/side-menu/components/side-menu-group/side-menu-group.component";
import { SideMenuGroupTitleComponent } from "@shared/components/side-menu/components/side-menu-group/components/side-menu-group-header/side-menu-group-title.component";
import { SideMenuGroupContentComponent } from "@shared/components/side-menu/components/side-menu-group/components/side-menu-group-content/side-menu-group-content.component";
import { CdkDrag, CdkDragDrop, CdkDropList, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { TableConfig } from '@shared/components/table-implementation/types/table-config';
import { OverviewMenuFieldComponent } from "./components/overview-menu-field/overview-menu-field.component";
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faGripLinesVertical } from '@fortawesome/pro-light-svg-icons';
import { provideTranslationScope, TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { TableColumn } from '@shared/components/table-implementation/types/column/header.type';
import { TableConfigService } from '@core/services/table/table-config.service';

@Component({
  selector: 'app-overview-settings-menu-fields',
  standalone: true,
  imports: [ SideMenuContentComponent,
    SideMenuGroupComponent,
    SideMenuGroupTitleComponent,
    SideMenuGroupContentComponent,
    CdkDropList,
    CdkDrag,
    DragDropModule,
    OverviewMenuFieldComponent,
    IconModule,
    TranslationPipeModule ],
  providers: [ provideTranslationScope("shared/overview-menu") ],
  templateUrl: './overview-settings-menu-fields.component.html',
  styleUrl: './overview-settings-menu-fields.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewSettingsMenuFieldsComponent {
  private _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private readonly _tableConfigService: TableConfigService = inject(TableConfigService);

  private _selectedTableConfig: Signal<TableConfig | undefined> = toSignal(this._tableConfigService.selectedConfig$);


  private _columns: Signal<TableColumn[]> = toSignal(this._tableConfigService.selectedConfig$.pipe(
    map((config: TableConfig | undefined) => config?.columnConfig.columns ?? [])
  ), { initialValue: [] });

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  public fixedColumns: Signal<TableColumn[]> = computed(() => this._columns().filter((header: TableColumn): boolean => (header.options.isFixed ? header.options.isVisible : false)));

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  public visibleColumns: Signal<TableColumn[]> = computed(() => this._columns().filter((header: TableColumn): boolean => !header.options.isFixed && header.options.isVisible));

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  public hiddenColumns: Signal<TableColumn[]> = computed(() => this._columns().filter((header: TableColumn): boolean => !header.options.isVisible));


  constructor () {
    this._faIconLibrary.addIcons(faGripLinesVertical);
  }

  protected changeVisibility (isVisible: boolean, column: TableColumn): void {
    this._tableConfigService.changeColumnVisibility(column.field, isVisible);
  }

  protected changeFixed (isFixed: boolean, column: TableColumn): void {
    this._tableConfigService.changeColumnFixed(column.field, isFixed);
  }

  protected rearrangeFields (dropEvent: CdkDragDrop<TableColumn[]>): void {
    if (!this._selectedTableConfig()) return;

    const columns: TableColumn[] = [ ...dropEvent.container.data ];
    const allColumns: TableColumn[] = [ ...this._columns() ];
    // eslint-disable-next-line @tseslint/no-non-null-assertion
    const newIndex: number = allColumns.findIndex((column: TableColumn) => column.field === columns[ dropEvent.currentIndex ]!.field);
    // eslint-disable-next-line @tseslint/no-non-null-assertion
    const previousIndex: number = allColumns.findIndex((column: TableColumn) => column.field === columns[ dropEvent.previousIndex ]!.field);

    if (newIndex !== -1 && previousIndex !== -1) {
      moveItemInArray(allColumns, previousIndex, newIndex);
      // eslint-disable-next-line @tseslint/no-non-null-assertion
      this._tableConfigService.patchColumnConfigLocally({ ...this._selectedTableConfig()!.columnConfig, columns: allColumns });
    }
  }
}
